// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Dropdown, TextInput, Select, Button, StatusBanner } from '@loa/ui-components';
import type { Policy, PolicyCreateDTO, PolicyUpdateDTO, PolicyOwnershipType } from '../types/policy';
import { brokerageConfigApi, type BrokerageProvider } from '../api/brokerageConfigApi';
import { advisorApi } from '../api/advisorApi';
import { usePolicyTypeOptions } from '../hooks/usePolicyTypeOptions';

type PolicyFormProps = Readonly<{
  policy?: Policy;
  onSubmit: (data: PolicyCreateDTO | PolicyUpdateDTO) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}>;

const ownershipTypeOptions = [
  { text: 'Solo (1 owner)', value: 'SOLO' },
  { text: 'Joint (2 owners)', value: 'JOINT' },
  { text: 'Group (2+ owners)', value: 'GROUP' },
];

export function PolicyForm({
  policy,
  onSubmit,
  onCancel,
  loading,
  error
}: PolicyFormProps) {
  const [formData, setFormData] = useState({
    policyNumber: '',
    category: '',
    carrier: '',
    ownershipType: 'SOLO' as PolicyOwnershipType,
    inAgency: false,
    loaInPlace: false,
  });

  const [providers, setProviders] = useState<BrokerageProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [brokerageId, setBrokerageId] = useState<string>('');

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { categoryOptions, loading: loadingPolicyTypes } = usePolicyTypeOptions(brokerageId, selectedProviderId);

  // Load brokerage ID and configuration data
  useEffect(() => {
    const loadBrokerageConfig = async () => {
      try {
        setLoadingConfig(true);

        // Get advisor profile to get brokerage ID
        const advisorProfile = await advisorApi.getProfile();
        const brokId = advisorProfile.brokerageId.toString();
        setBrokerageId(brokId);

        // Load enabled providers
        const providersResponse = await brokerageConfigApi.getEnabledProviders(brokId);
        setProviders(providersResponse.content);
      } catch (err) {
        console.error('Failed to load brokerage configuration:', err);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadBrokerageConfig();
  }, []);

  // Initialize form data when editing a policy
  useEffect(() => {
    if (policy) {
      setFormData({
        policyNumber: policy.policyNumber || '',
        category: policy.category || '',
        carrier: policy.carrier || '',
        ownershipType: policy.ownershipType || 'SOLO',
        inAgency: policy.inAgency || false,
        loaInPlace: policy.loaInPlace || false,
      });
    }
  }, [policy]);

  // Set selected provider after providers are loaded (for editing)
  useEffect(() => {
    if (policy && providers.length > 0 && !loadingConfig) {
      if (policy.providerId) {
        setSelectedProviderId(policy.providerId);
      } else if (policy.carrier) {
        const matchingProvider = providers.find(
          p => p.provider.name.toLowerCase() === policy.carrier.toLowerCase()
        );
        if (matchingProvider) {
          setSelectedProviderId(matchingProvider.providerId);
        }
      }
    }
  }, [policy, providers, loadingConfig]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!selectedProviderId) {
      errors.provider = 'Provider is required';
    }

    if (!formData.category) {
      errors.category = 'Policy type is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data = {
      policyNumber: formData.policyNumber.trim() || undefined,
      category: formData.category,
      providerId: selectedProviderId,
      status: 'active' as const,
      ownershipType: formData.ownershipType,
      inAgency: formData.inAgency,
      loaInPlace: formData.loaInPlace,
    };

    await onSubmit(data);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedProviderId(providerId);

    const selectedProvider = providers.find(p => p.providerId === providerId);
    if (selectedProvider) {
      setFormData(prev => ({
        ...prev,
        carrier: selectedProvider.provider.name
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        carrier: ''
      }));
    }

    if (validationErrors.provider) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.provider;
        return newErrors;
      });
    }
  };

  const providerOptions = providers.map(provider => ({
    value: provider.providerId,
    text: provider.provider.name
  }));

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-lg">
      {error && (
        <StatusBanner
          variant="error"
          message={error}
          visible
          position="inline"
          dismissible={false}
        />
      )}

      <Dropdown
        label="Provider"
        placeholder="Select a provider"
        options={providerOptions}
        value={selectedProviderId}
        onChange={handleProviderChange}
        search
        fluid
        required
        disabled={loadingConfig || !!policy}
        error={validationErrors.provider}
      />

      <Dropdown
        label="Category"
        placeholder="Select a category"
        options={categoryOptions}
        value={formData.category}
        onChange={(value) => handleChange('category', value)}
        fluid
        required
        disabled={loadingConfig || loadingPolicyTypes || !selectedProviderId}
        error={validationErrors.category}
      />

      <TextInput
        label="Policy Number (Optional)"
        placeholder="POL-2024-001"
        value={formData.policyNumber}
        onChange={(e) => handleChange('policyNumber', e.target.value)}
        fluid
      />

      <Select
        label="Ownership Type"
        options={ownershipTypeOptions}
        value={formData.ownershipType}
        onChange={(e) => handleChange('ownershipType', e.target.value)}
        fluid
        required
      />

      <Select
        label="Agency"
        options={[
          { text: 'Not in Agency', value: 'false' },
          { text: 'In Agency', value: 'true' },
        ]}
        value={formData.inAgency ? 'true' : 'false'}
        onChange={(e) => setFormData(prev => ({ ...prev, inAgency: e.target.value === 'true' }))}
        fluid
      />

      <Select
        label="LOA"
        options={[
          { text: 'Not in Place', value: 'false' },
          { text: 'LOA in Place', value: 'true' },
        ]}
        value={formData.loaInPlace ? 'true' : 'false'}
        onChange={(e) => setFormData(prev => ({ ...prev, loaInPlace: e.target.value === 'true' }))}
        fluid
      />

      <div className="flex gap-sm justify-end pt-sm">
        <Button variant="outlined" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="filled"
          type="submit"
          loading={loading}
          disabled={loadingConfig}
        >
          {policy ? 'Update Policy' : 'Create Policy'}
        </Button>
      </div>
    </form>
  );
}
